import { useState } from "react";
import useSWR from "swr";
import { setUrlHash } from "@/lib/navigation";

import { ApiTimeout, useBackendApi } from "@/hooks/fetch";
import { useOpenConfirmDialog } from "@/hooks/dialogs";
import { txToast } from "@/components/TxToaster";
import { useAdminPerms } from "@/hooks/auth";
import { SYM_RESET_CONFIG, type SettingsCardContext, type SettingsCardInfo, type SettingsCardProps, type SettingsTabInfo } from "./utils";
import type { GetConfigsResp, PartialTxConfigs, SaveConfigsReq, SaveConfigsResp } from "@shared/otherTypes";
import {
    Settings2Icon, ServerIcon, ShieldBanIcon, ListChecksIcon,
    MessageSquareIcon, GamepadIcon, ChevronRightIcon,
} from "lucide-react";

import SettingsTab from "./SettingsTab";
import ConfigCardBans from "./tabCards/bans";
import ConfigCardDiscord from "./tabCards/discord";
import ConfigCardFxserver from "./tabCards/fxserver";
import ConfigCardGameMenu from "./tabCards/gameMenu";
import ConfigCardGameNotifications from "./tabCards/gameNotifications";
import ConfigCardGeneral from "./tabCards/general";
import ConfigCardWhitelist from "./tabCards/whitelist";
import SettingsCardTemplate from "./tabCards/_template";
import SettingsCardBlank from "./tabCards/_blank";
import { PageHeaderChangelog } from "@/components/page-header";



//Tab configuration
const settingsTabsBase = [
    { name: 'General', Component: ConfigCardGeneral }, //TODO: cards [Server Listing, txAdmin]
    { name: 'FXServer', Component: ConfigCardFxserver },
    { name: 'Bans', Component: ConfigCardBans },
    { name: 'Whitelist', Component: ConfigCardWhitelist },
    { name: 'Discord', Component: ConfigCardDiscord },
    {
        name: 'Game',
        cards: [
            { name: 'Menu', Component: ConfigCardGameMenu },
            { name: 'Notifications', Component: ConfigCardGameNotifications },
        ]
    },
    //Dev only
    // { name: 'Template', Component: SettingsCardTemplate },
    // { name: 'Blank', Component: SettingsCardBlank },
]


//Types
type SettingGroup = {
    ctx: SettingsTabInfo & SettingsCardInfo;
    Component: React.FC<SettingsCardProps>;
};
type SettingTabMulti = {
    ctx: SettingsTabInfo;
    cards: SettingGroup[];
};
type SettingTabSingle = SettingGroup;
export type SettingTabsDatum = SettingTabMulti | SettingTabSingle;


//Massaging the data into the expected format
const nameToId = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '-');
export const settingsTabs: SettingTabsDatum[] = settingsTabsBase.map((tab) => {
    const tabCtx = {
        tabId: nameToId(tab.name),
        tabName: tab.name,
    } satisfies SettingsTabInfo;
    if ('cards' in tab && tab.cards) {
        return {
            ctx: tabCtx,
            cards: tab.cards.map((card) => ({
                ctx: {
                    ...tabCtx,
                    cardId: `${tabCtx.tabId}-${nameToId(card.name)}`,
                    cardName: card.name,
                    cardTitle: `${tabCtx.tabName} ${card.name}`,
                },
                Component: card.Component,
            } satisfies SettingGroup)),
        } satisfies SettingTabMulti;
    } else {
        return {
            ctx: {
                ...tabCtx,
                cardId: tabCtx.tabId,
                cardName: tabCtx.tabName,
                cardTitle: tabCtx.tabName,
            },
            Component: tab.Component,
        } satisfies SettingTabSingle;
    }
});


export default function SettingsPage() {
    const [cardPendingSave, setCardPendingSave] = useState<SettingsCardContext | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const openConfirmDialog = useOpenConfirmDialog();
    const { hasPerm } = useAdminPerms();

    //Check for default tab in URL hash
    const [tab, setTab] = useState(() => {
        const pageHash = window.location?.hash.slice(1);
        return settingsTabs.find(tab => tab.ctx.tabId === pageHash)?.ctx.tabId ?? settingsTabs[0].ctx.tabId;
    });


    //API stuff
    const queryApi = useBackendApi<GetConfigsResp>({
        method: 'GET',
        path: `/settings/configs`,
        throwGenericErrors: true,
    });
    const saveApi = useBackendApi<SaveConfigsResp, SaveConfigsReq>({
        method: 'POST',
        path: `/settings/configs/:card`,
        throwGenericErrors: true,
    });

    const swr = useSWR('/settings/configs', async () => {
        const data = await queryApi({});
        if (!data) throw new Error('No data returned');
        return data;
    }, {
        revalidateOnMount: true,
        revalidateOnFocus: false,
    });


    //Handlers
    const saveChanges = async (source: SettingsCardContext, changes: PartialTxConfigs) => {
        if (isSaving) return;
        const toastId = txToast.loading(`Saving ${source.cardTitle} settings...`, { id: 'settingsSave' });
        setIsSaving(true);
        try {
            if (!swr.data) throw new Error('Cannot save changes without swr.data.');
            const resetKeys: string[] = [];
            for (const [scopeName, scopeData] of Object.entries(changes)) {
                for (const [configKey, configValue] of Object.entries(scopeData)) {
                    if (configValue === SYM_RESET_CONFIG) {
                        resetKeys.push(`${scopeName}.${configKey}`);
                    }
                }
            }
            const saveResp = await saveApi({
                pathParams: { card: source.cardId },
                data: { resetKeys, changes },
                timeout: source.cardId === 'discord'
                    ? ApiTimeout.REALLY_REALLY_LONG
                    : ApiTimeout.LONG,
                toastId,
            });
            if (!saveResp) throw new Error('empty_response');
            if (saveResp.type === 'error') return; //the fetcher will handle the error
            if (!saveResp.stored) throw new Error('no_stored_data');
            if (!saveResp.changelog) throw new Error('no_changelog_data');
            swr.mutate({
                ...swr.data,
                storedConfigs: saveResp.stored,
                changelog: saveResp.changelog,
            }, false);
            setCardPendingSave(null);
        } catch (error) {
            txToast.error({
                title: `Error saving ${source.cardTitle} settings:`,
                msg: (error as any).message,
            }, { id: toastId });
        } finally {
            setIsSaving(false);
        }
    }

    const switchTab = (newTab: string) => {
        setCardPendingSave(null);
        setTab(newTab);
        setUrlHash(newTab);
    }

    //If switching tabs with unsaved changes, ask for confirmation
    const handleTabChange = (newTab: string) => {
        if (cardPendingSave && newTab && newTab !== cardPendingSave?.tabId) {
            openConfirmDialog({
                title: 'Discard Changes',
                actionLabel: 'Discard',
                confirmBtnVariant: 'destructive',
                message: (<>
                    You have unsaved changes in the <strong>{cardPendingSave.cardTitle}</strong> tab. <br />
                    Are you sure you want to discard them?
                </>),
                onConfirm: () => {
                    switchTab(newTab);
                },
            });
        } else {
            switchTab(newTab);
        }
    }


    const TAB_ICONS: Record<string, React.ReactNode> = {
        'general':   <Settings2Icon className="h-4 w-4" />,
        'fxserver':  <ServerIcon className="h-4 w-4" />,
        'bans':      <ShieldBanIcon className="h-4 w-4" />,
        'whitelist': <ListChecksIcon className="h-4 w-4" />,
        'discord':   <MessageSquareIcon className="h-4 w-4" />,
        'game':      <GamepadIcon className="h-4 w-4" />,
    };

    const pageCtx = {
        apiData: swr.data,
        isReadOnly: swr.isLoading || isSaving || !swr.data || !hasPerm('settings.write'),
        isLoading: swr.isLoading,
        isSaving,
        swrError: swr.error ? swr.error.message : undefined,
        cardPendingSave,
        setCardPendingSave,
        saveChanges,
    };

    const activeTab = settingsTabs.find(t => t.ctx.tabId === tab);

    return (
        <div className="flex h-full min-h-0 w-full">
            {/* ── Left sidebar ── */}
            <aside className="w-[220px] shrink-0 flex flex-col border-r"
                style={{ borderColor: 'var(--sc-border)', background: 'var(--sc-bg-surface)' }}>

                {/* Sidebar header */}
                <div className="px-5 py-5 border-b" style={{ borderColor: 'var(--sc-border)' }}>
                    <div className="text-[9px] font-mono tracking-[0.2em] uppercase mb-1"
                        style={{ color: 'rgba(0,255,209,0.35)' }}>
                        System
                    </div>
                    <h1 className="text-lg font-black tracking-tight"
                        style={{ color: 'var(--sc-text)', letterSpacing: '-0.03em' }}>
                        Settings
                    </h1>
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: 'var(--sc-muted)' }}>
                        Server configuration
                    </p>
                </div>

                {/* Nav items */}
                <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5 overflow-y-auto">
                    {settingsTabs.map((t) => {
                        const isActive = t.ctx.tabId === tab;
                        const hasPending = cardPendingSave?.tabId === t.ctx.tabId;
                        return (
                            <button
                                key={t.ctx.tabId}
                                onClick={() => handleTabChange(t.ctx.tabId)}
                                className="relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[12px] font-semibold text-left transition-all group"
                                style={{
                                    background: isActive ? 'rgba(0,255,209,0.07)' : 'transparent',
                                    color: isActive ? 'var(--sc-cyan)' : 'var(--sc-muted)',
                                    border: isActive ? '1px solid rgba(0,255,209,0.14)' : '1px solid transparent',
                                }}
                                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = 'var(--sc-text)'; }}
                                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = 'var(--sc-muted)'; }}
                            >
                                {/* Active bar */}
                                {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 rounded-r-full"
                                        style={{ background: 'var(--sc-cyan)', boxShadow: '0 0 8px var(--sc-cyan)' }} />
                                )}
                                <span style={{ color: isActive ? 'var(--sc-cyan)' : 'inherit', opacity: isActive ? 1 : 0.6 }}>
                                    {TAB_ICONS[t.ctx.tabId] ?? <Settings2Icon className="h-4 w-4" />}
                                </span>
                                <span className="flex-1">{t.ctx.tabName}</span>
                                {hasPending && (
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ background: 'var(--sc-warn)', boxShadow: '0 0 4px var(--sc-warn)' }} />
                                )}
                                {isActive && <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 opacity-50" />}
                            </button>
                        );
                    })}
                </nav>

                {/* Last updated */}
                {swr?.data?.changelog && (
                    <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--sc-border)' }}>
                        <PageHeaderChangelog changelogData={swr.data.changelog} />
                    </div>
                )}
            </aside>

            {/* ── Content panel ── */}
            <div className="flex-1 min-w-0 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>
                {/* Content header */}
                <div className="mb-6 pb-4 border-b flex items-center justify-between"
                    style={{ borderColor: 'var(--sc-border)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                            style={{ background: 'rgba(0,255,209,0.08)', border: '1px solid rgba(0,255,209,0.14)', color: 'var(--sc-cyan)' }}>
                            {activeTab ? TAB_ICONS[activeTab.ctx.tabId] ?? <Settings2Icon className="h-4 w-4" /> : <Settings2Icon className="h-4 w-4" />}
                        </div>
                        <div>
                            <h2 className="text-base font-black" style={{ color: 'var(--sc-text)' }}>
                                {activeTab?.ctx.tabName ?? 'Settings'}
                            </h2>
                            <p className="text-[10px] font-mono" style={{ color: 'var(--sc-muted)' }}>
                                {cardPendingSave?.tabId === tab ? '· unsaved changes' : 'Configure server settings'}
                            </p>
                        </div>
                    </div>
                    {swr.isLoading && (
                        <div className="flex items-center gap-2 text-[10px] font-mono" style={{ color: 'var(--sc-muted)' }}>
                            <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin"
                                style={{ borderColor: 'rgba(0,255,209,0.4)', borderTopColor: 'transparent' }} />
                            Loading...
                        </div>
                    )}
                </div>

                {/* Tab content */}
                {activeTab && (
                    <SettingsTab tab={activeTab} pageCtx={pageCtx} />
                )}
            </div>
        </div>
    )
}
